import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ProductAditionalData {
  country_id: string;
  price_local_currency: number;
  price_usdt: number;
  delivery_time_days: number;
  loyalty_points: number;
  influencer_bonus: number;
  _id: string;
  dropi_product_id: string;
}

export interface Category {
  id: string;
  name: string;
}

export interface Product {
  _id: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  category: Category;
  images: string[];
  mainImage: string;
  aditionalData: ProductAditionalData[];
  status: boolean;
  created_at: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  quantity?: number;
  currency?: string; // Moneda local del país (COP, USD, etc.)
}

interface CartState {
  items: Product[];
  addItem: (product: Product) => void;
  removeItem: (productId: string | 'all') => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      
      addItem: (product: Product) => {
        const { items } = get();
        const existingItem = items.find(item => item._id === product._id);
        
        if (existingItem) {
          // If item already exists, increase quantity
          set({
            items: items.map(item => 
              item._id === product._id 
                ? { ...item, quantity: (item.quantity || 1) + 1 } 
                : item
            )
          });
        } else {
          // Add new item with quantity 1
          set({ items: [...items, { ...product, quantity: 1 }] });
        }
      },
      
      removeItem: (productId: string | 'all') => {
        if (productId === 'all') {
          // If 'all' is passed, clear the entire cart
          set({ items: [] });
        } else {
          // Remove specific item
          const { items } = get();
          set({ items: items.filter(item => item._id !== productId) });
        }
      },
      
      updateQuantity: (productId: string, quantity: number) => {
        const { items } = get();
        if (quantity <= 0) {
          // If quantity is 0 or negative, remove the item
          set({ items: items.filter(item => item._id !== productId) });
        } else {
          // Update quantity
          set({
            items: items.map(item => 
              item._id === productId ? { ...item, quantity } : item
            )
          });
        }
      },
      
      clearCart: () => set({ items: [] }),
      
      getTotalItems: () => {
        const { items } = get();
        return items.reduce((total, item) => total + (item.quantity || 1), 0);
      },
      
      getTotalPrice: () => {
        const { items } = get();
        return items.reduce((total, item) => {
          const price = item.aditionalData[0]?.price_usdt || 0;
          return total + price * (item.quantity || 1);
        }, 0);
      }
    }),
    {
      name: 'multyhealth-cart',
    }
  )
);
